import { and, not } from 'tio-ui/utilities';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Text from 'tio-ui/components/text';
import awsUrl from 'tio-employee/helpers/aws-url';
import Component from '@glimmer/component';
import ShouldIRefinance from '../should-i-refinance';
import SectionHeader from 'tio-ui/components/section-header';
import SectionPanel from 'tio-ui/components/section-panel';
import AlertBanner from 'tio-ui/components/alert-banner';
import Card from 'tio-ui/components/card';
import FeatureCard from 'tio-ui/components/feature-card';
import FaqAccordion from 'tio-ui/components/faq-accordion';
import type { AccordionItem } from 'tio-ui/components/faq-accordion';
import StepCard from 'tio-ui/components/step-card';
import Button from 'tio-ui/components/button';
import HighlightCard from 'tio-ui/components/highlight-card';
import type { IntlService } from 'ember-intl';
import type PartnerService from 'tio-employee/services/partner';
import {
  PhArrowsClockwise,
  PhTrendDown,
  PhCurrencyDollar,
  PhRocket,
  PhQuestion,
  PhHandshake,
  PhClipboardText,
  PhArrowRight,
} from 'ember-phosphor-icons';

interface S {
  Element: HTMLElement;
}

export default class RepaymentOptionsRefinancingComponent extends Component<S> {
  @service declare partner: PartnerService;
  @service declare intl: IntlService;

  @tracked recommendRefinance = false;

  recommendation = (status: boolean) => {
    this.recommendRefinance = status;
  };

  get considerationItems(): AccordionItem[] {
    return [
      {
        question: this.intl.t('wellness_insights.refinancing_else_to_know'),
        answer: this.intl.t('wellness_insights.refinancing_else_to_know_description'),
      },
      {
        question: 'Payment Flexibility',
        answer: this.intl.t(
          'wellness_insights.refinancing_else_to_know_description_payment_flexibility'
        ),
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="space-y-8" ...attributes>

      {{! Fed Reserve Warnings }}
      {{#if this.partner.shouldShowCustomRefinancingWarning}}
        <AlertBanner @intent="info" @title="Important Notice">
          {{t "wellness_insights.fed_reserve_acknowledgement_1" htmlSafe=true}}
        </AlertBanner>
        <AlertBanner @intent="warning" @title="Please Note">
          {{t "wellness_insights.fed_reserve_acknowledgement_2" htmlSafe=true}}
        </AlertBanner>
      {{/if}}

      {{! What is Refinancing }}
      <div>
        <SectionHeader
          @icon={{PhArrowsClockwise}}
          @title="What is Refinancing?"
          @subtitle="Create a new loan with better terms"
          @iconClass="tio-fg-brand"
        />
        <div
          class="{{if
              this.partner.shouldShowRefinanceApply
              'grid grid-cols-1 md:grid-cols-3 gap-6'
            }}"
        >
          <Card @padding="lg" class="{{if this.partner.shouldShowRefinanceApply 'md:col-span-2'}}">
            <Text class="tio-fg-secondary leading-relaxed">
              {{t "wellness_insights.refinancing_description"}}
            </Text>
          </Card>

          {{#if this.partner.isLendkey}}
            <HighlightCard
              @icon={{PhHandshake}}
              @title="Partner Marketplace"
              @value="LendKey"
              @description="Check your new rate in 2 minutes"
              @color="blue"
            >
              <:extra>
                <a
                  href={{this.partner.lendkeyHref}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="tio-inline-row gap-1 tio-text-sm-medium tio-fg-info hover:tio-fg-info-body"
                >
                  Visit Marketplace
                  <PhArrowRight class="tio-icon-sm" />
                </a>
              </:extra>
            </HighlightCard>
          {{/if}}
        </div>
      </div>

      {{! Benefits }}
      <div>
        <SectionHeader
          @icon={{PhTrendDown}}
          @title="Benefits of Refinancing"
          @subtitle="Why refinancing could work for you"
          @iconClass="tio-fg-success"
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FeatureCard
            @icon={{PhTrendDown}}
            @title="Lower Interest Rate"
            @description="Refinancing your loans can give you a significantly lower interest rate, reducing the overall cost of your debt."
            @color="emerald"
          />
          <FeatureCard
            @icon={{PhCurrencyDollar}}
            @title="Save Money"
            @description="Since you pay less interest over time, you'll save money and can redirect those funds toward other financial goals."
            @color="blue"
          />
          <FeatureCard
            @icon={{PhRocket}}
            @title="Faster Payoff"
            @description="With flexible repayment terms from 5 to 20 years, you can choose a plan that helps you become debt-free sooner."
            @color="violet"
          />
        </div>

        {{#if this.partner.isLendkey}}
          <Card @padding="md" class="mt-4">
            <div class="tio-row-loose">
              <div class="flex-1">
                <Text class="tio-fg-secondary">
                  {{t
                    "wellness_insights.refinancing_download_guide"
                    link=(awsUrl "staticData/Ultimate_Guide_to_Refi.pdf")
                    htmlSafe=true
                  }}
                </Text>
              </div>
              <img
                src="/assets/images/lend-key-guide.png"
                width="80"
                height="80"
                alt="LendKey Refinancing Guide"
                class="rounded-lg"
              />
            </div>
          </Card>
        {{/if}}
      </div>

      {{! Should I Refinance Quiz }}
      <SectionPanel
        @icon={{PhQuestion}}
        @title="Should I Refinance?"
        @iconColor="indigo"
        @roundedXl={{true}}
      >
        <:body>
          <Text @size="sm" class="tio-fg-secondary mb-4">
            Answer the questions below to get a personalized recommendation.
          </Text>
          <ShouldIRefinance @recommendation={{this.recommendation}} />

          <div class="mt-6">
            {{#if (and this.recommendRefinance (not this.partner.isLendkey))}}
              <AlertBanner @intent="success" @title="Refinancing could be a great option for you">
                {{t "wellness_insights.refinancing_do_recommend_description_without_lendkey"}}
              </AlertBanner>
            {{else if this.recommendRefinance}}
              <AlertBanner @intent="success" @title="Refinancing could be a great option for you">
                {{t
                  "wellness_insights.refinancing_do_recommend_description"
                  link="http://lendkey.com"
                  htmlSafe=true
                }}
              </AlertBanner>
            {{else}}
              <AlertBanner @intent="tip" @title="We don't recommend refinancing">
                {{t "wellness_insights.refinancing_dont_recommend_description"}}
                <LinkTo
                  @route="authenticated.repaying-student-debt.repayment-strategy-finder"
                  class="font-medium tio-fg-brand hover:tio-fg-brand"
                >
                  {{t "wellness_insights.repayment_options_need_more_help_description_link"}}
                </LinkTo>
              </AlertBanner>
            {{/if}}
          </div>
        </:body>
      </SectionPanel>

      {{! Other Considerations }}
      <div>
        <SectionHeader
          @icon={{PhClipboardText}}
          @title="Other Considerations"
          @subtitle="Important things to know before refinancing"
          @iconClass="tio-fg-warning"
        />
        <FaqAccordion @items={{this.considerationItems}} />
      </div>

      {{! How to Apply (LendKey) }}
      {{#if this.partner.isLendkey}}
        <div>
          <SectionHeader
            @icon={{PhRocket}}
            @title="How to Apply"
            @subtitle="Get started with LendKey in four simple steps"
            @iconClass="tio-fg-info"
          />
          <div class="space-y-3">
            <StepCard
              @step={{1}}
              @title="Gather Your Documents"
              @description="Prepare a government-issued ID, 2 recent paystubs or last year's W9, and your most recent loan statement."
            />
            <StepCard
              @step={{2}}
              @title="Fill Out the Application"
              @description="Complete one form at LendKey to see rates from hundreds of non-profit credit unions and community banks in about 2 minutes."
            />
            <StepCard
              @step={{3}}
              @title="Upload & Verify"
              @description="Upload your documents for review by a loan specialist. Verification takes 1-2 business days."
            />
            <StepCard
              @step={{4}}
              @title="Sign & Save"
              @description="Receive an email with a DocuSign link to your 2-3 page loan agreement. Sign digitally and you're all set with your new, lower rate!"
            />
          </div>

          <Card @padding="md" class="mt-4">
            <div class="tio-row-between">
              <div class="tio-row">
                <img
                  src="/assets/images/lend-key.svg"
                  alt="LendKey"
                  width="100"
                  height="24"
                  loading="lazy"
                />
                <Text @size="sm" class="tio-fg-secondary">
                  Our trusted refinancing partner
                </Text>
              </div>
              <a href={{this.partner.lendkeyHref}} target="_blank" rel="noopener noreferrer">
                <Button @intent="primary" @size="sm">
                  Get Started
                  <PhArrowRight class="tio-icon-sm ml-1" />
                </Button>
              </a>
            </div>
          </Card>
        </div>
      {{/if}}
    </div>
  </template>
}
